import { useCallback, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { LandholdingDetails, LandholdingDraft, ProjectDraft, SelectData } from "../../../../../models";
import { createGroup, createProject, CreateProjectRequest } from "../../../../../service/project";
import { ServiceError, Status } from "../../../../../service/Shared";
import { getErrorMessageFromCode } from "../../../../../service/ValidationErrorFormatter";
import { useProjectReferenceOptions } from "../../../../../utils";
import { RadioChangeEvent } from "../../../../../widget/forms/Input/RadioButtonGroup/components";

type LandholdingChoice = "yes" | "no";

interface UseCreateProjectPanelReturnData {
  landholdingChoice: LandholdingChoice;
  landholdingDraft: LandholdingDraft;
  projectDetails: ProjectDraft[];
  countryOptions: SelectData | undefined;
  getProjectTypeOptions: (standardUuid: string) => SelectData;
  errors: ServiceError[];
  isHandleSaveLoading: boolean;
  getStandardOptions: (projectTypeUuid: string) => SelectData;
  updateLandholdingChoice: (event: RadioChangeEvent) => void;
  updateLandholdingField: <K extends keyof LandholdingDraft>(field: K) => (value: LandholdingDraft[K]) => void;
  updateLandholdingDetails: (details: LandholdingDetails) => void;
  updateProjectField: <K extends keyof ProjectDraft>(id: string, field: K, value: ProjectDraft[K]) => void;
  updateProjectTypeField: (id: string, value: string) => void;
  updateStandardField: (id: string, value: string) => void;
  addProjectDetails: () => void;
  removeProjectDetails: (id: string) => void;
  handleSave: () => void;
}

const createProjectDraft = (): ProjectDraft => ({
  key: uuidv4(), // Unique ref for landholding + react
  name: "",
  projectTypeUuid: "",
  standardUuid: "",
  developerUuid: "",
  region: "",
  postcode: "",
  latitude: "",
  longitude: "",
  countryCode: "",
  areaNetHa: "",
});

const createLandholdingDraft = (): LandholdingDraft => ({
  key: uuidv4(), // Unique ref for landholding + react
  name: "",
  reference: "",
  referenceId: "",
  region: "",
  latitude: "",
  longitude: "",
  countryCode: "",
  projectKeys: [],
});

const formatCreateProjectErrors = (projectIndex: number, serviceErrors: ServiceError[] | undefined): ServiceError[] =>
  (serviceErrors ?? []).map((error) => {
    let { message } = error;
    message = getErrorMessageFromCode(error.code, error.parameters ?? { property: error.property });
    if (error.code === "VALIDATION_ATTRIBUTE_REQUIRED") {
      switch (error.property) {
        case "StandardUuid":
          return { ...error, message: `Project ${projectIndex + 1}: Standard is a required field` };
        case "ProjectTypeUuid":
          return { ...error, message: `Project ${projectIndex + 1}: Project type is a required field` };
        case "DisplayName":
          return { ...error, message: `Project ${projectIndex + 1}: Project name is a required field` };
        default:
          break;
      }
    }
    return {
      ...error,
      message: `Project ${projectIndex + 1}: ${message}`,
    };
  });

const formatCreateGroupErrors = (serviceErrors: ServiceError[] | undefined): ServiceError[] =>
  (serviceErrors ?? []).map((error) => {
    let { message } = error;
    message = getErrorMessageFromCode(error.code, error.parameters ?? { property: error.property });
    return { ...error, message };
  });

const buildCreateProjectRequest = (project: ProjectDraft): Omit<CreateProjectRequest, "validateOnly"> => ({
  developerUuid: null,
  displayName: project.name,
  projectTypeUuid: project.projectTypeUuid,
  standardUuid: project.standardUuid,
  addressCountryCode: project.countryCode || null,
  addressPostcode: project.postcode || null,
  addressRegion: project.region || null,
  areaNetHa: project.areaNetHa ? Number(project.areaNetHa) : null,
  locationCoordinates:
    project.latitude || project.longitude ? `${project.latitude || ""},${project.longitude || ""}` : null,
});

export const useCreateProjectPanel = (
  onSave: (projects: ProjectDraft[], landholding: LandholdingDraft, isLandholding: boolean) => void,
  onClose: () => void
): UseCreateProjectPanelReturnData => {
  const [errors, setErrors] = useState<ServiceError[]>([]);
  const [landholdingChoice, setLandholdingChoice] = useState<LandholdingChoice>("no");
  const [isHandleSaveLoading, setIsHandleSaveLoading] = useState(false);
  const [projectDetails, setProjectDetails] = useState<ProjectDraft[]>([createProjectDraft()]);
  const [landholdingDraft, setLandholdingDraft] = useState<LandholdingDraft>(createLandholdingDraft());
  const options = useProjectReferenceOptions();

  const updateLandholdingChoice = useCallback((event: RadioChangeEvent) => {
    const value = event.target.value as LandholdingChoice;
    setLandholdingChoice(value);
    if (value === "no") {
      setProjectDetails([createProjectDraft()]);
      setLandholdingDraft(createLandholdingDraft());
    }
  }, []);

  const updateLandholdingField = useCallback(
    <K extends keyof LandholdingDraft>(field: K) =>
      (value: LandholdingDraft[K]) => {
        setLandholdingDraft((prev) => ({ ...prev, [field]: value }));
      },
    []
  );

  const updateLandholdingDetails = useCallback((details: LandholdingDetails) => {
    setLandholdingDraft((prev) => ({
      ...prev,
      name: details.name,
      reference: details.reference ?? "",
      referenceId: details.referenceId,
      region: details.region,
      latitude: details.latitude ?? "",
      longitude: details.longitude ?? "",
      countryCode: details.countryCode ?? "",
    }));
  }, []);

  const updateProjectField = useCallback(
    <K extends keyof ProjectDraft>(key: string, field: K, value: ProjectDraft[K]) => {
      setErrors([]);
      setProjectDetails((prev) =>
        prev.map((project) => (project.key === key ? { ...project, [field]: value } : project))
      );
    },
    [setProjectDetails]
  );

  const updateProjectTypeField = useCallback(
    (key: string, value: string) => {
      setErrors([]);
      setProjectDetails((prev) =>
        prev.map((project) => {
          if (project.key !== key) {
            return project;
          }

          const allowedStandards = options.standardOptionsByProjectType[value] ?? [];
          const isCurrentStandardAllowed =
            options.isLoading ||
            !value ||
            !project.standardUuid ||
            allowedStandards.some((standard) => standard.key === project.standardUuid);

          return {
            ...project,
            projectTypeUuid: value,
            standardUuid: isCurrentStandardAllowed ? project.standardUuid : "",
          };
        })
      );
    },
    [options.standardOptionsByProjectType, options.isLoading]
  );

  const updateStandardField = useCallback(
    (key: string, value: string) => {
      setErrors([]);
      setProjectDetails((prev) =>
        prev.map((project) => {
          if (project.key !== key) {
            return project;
          }

          const allowedProjectTypes = options.projectTypeOptionsByStandard[value] ?? [];
          const isCurrentProjectTypeAllowed =
            options.isLoading ||
            !value ||
            !project.projectTypeUuid ||
            allowedProjectTypes.some((projectType) => projectType.key === project.projectTypeUuid);

          return {
            ...project,
            standardUuid: value,
            projectTypeUuid: isCurrentProjectTypeAllowed ? project.projectTypeUuid : "",
          };
        })
      );
    },
    [options.projectTypeOptionsByStandard, options.isLoading]
  );

  const getStandardOptions = useCallback(
    (projectTypeUuid: string): SelectData => options.getStandardOptions(projectTypeUuid),
    [options.getStandardOptions]
  );

  const getProjectTypeOptions = useCallback(
    (standardUuid: string): SelectData => options.getProjectTypeOptions(standardUuid),
    [options.getProjectTypeOptions]
  );

  const addProjectDetails = useCallback(() => {
    setErrors([]);
    setProjectDetails((prev) => [...prev, createProjectDraft()]);
  }, []);

  const removeProjectDetails = useCallback((key: string) => {
    setErrors([]);
    setProjectDetails((prev) => prev.filter((project) => project.key !== key));
  }, []);

  const handleSave = useCallback(async () => {
    setErrors([]);
    setIsHandleSaveLoading(true);

    if (landholdingChoice === "yes") {
      for (let projectIndex = 0; projectIndex < projectDetails.length; projectIndex += 1) {
        // eslint-disable-next-line no-await-in-loop
        const validationResponse = await createProject({
          ...buildCreateProjectRequest(projectDetails[projectIndex]),
          validateOnly: true,
        });

        if (validationResponse.status === Status.Error) {
          setErrors(formatCreateProjectErrors(projectIndex, validationResponse.errors));
          setIsHandleSaveLoading(false);
          return;
        }
      }

      const response = await createGroup({
        name: landholdingDraft.name,
        externalReference: landholdingDraft.referenceId,
        externalParty: landholdingDraft.reference,
        addressRegion: landholdingDraft.region || null,
        addressCountryCode: landholdingDraft.countryCode || null,
        locationCoordinates:
          landholdingDraft.latitude || landholdingDraft.longitude
            ? `${landholdingDraft.latitude || ""},${landholdingDraft.longitude || ""}`
            : null,
        type: "Landholding",
        projects: projectDetails.map((p) => buildCreateProjectRequest(p)),
      });

      if (response.status === Status.Error) {
        setErrors(formatCreateGroupErrors(response.errors));
        setIsHandleSaveLoading(false);
        return;
      }

      const savedLandholdingDraft = {
        ...landholdingDraft,
        key: response.data?.uuid ?? landholdingDraft.key,
        projectKeys: projectDetails.map((p) => p.key),
      };

      onSave(projectDetails, savedLandholdingDraft, true);
    } else {
      const savedProjects: ProjectDraft[] = [];

      for (let projectIndex = 0; projectIndex < projectDetails.length; projectIndex += 1) {
        const project = projectDetails[projectIndex];
        // eslint-disable-next-line no-await-in-loop
        const response = await createProject({
          ...buildCreateProjectRequest(project),
          validateOnly: false,
        });

        if (response.status === Status.Error) {
          if (savedProjects.length > 0) {
            setProjectDetails(projectDetails.slice(projectIndex));
          }

          setErrors(formatCreateProjectErrors(projectIndex, response.errors));
          setIsHandleSaveLoading(false);
          return;
        }

        if (response.data?.projectUuid) {
          savedProjects.push({ ...project, key: response.data.projectUuid, projectUuid: response.data.projectUuid });
        }
      }

      if (savedProjects.length === 0) {
        setIsHandleSaveLoading(false);
        return;
      }

      onSave(savedProjects, landholdingDraft, false);
    }

    setLandholdingChoice("no");
    setProjectDetails([createProjectDraft()]);
    setLandholdingDraft(createLandholdingDraft());
    onClose();
    setIsHandleSaveLoading(false);
  }, [landholdingChoice, landholdingDraft, onClose, onSave, projectDetails]);

  return {
    landholdingChoice,
    landholdingDraft,
    projectDetails,
    countryOptions: options.countryOptions,
    getProjectTypeOptions,
    errors,
    isHandleSaveLoading,
    getStandardOptions,
    updateLandholdingChoice,
    updateLandholdingField,
    updateLandholdingDetails,
    updateProjectField,
    updateProjectTypeField,
    updateStandardField,
    addProjectDetails,
    removeProjectDetails,
    handleSave,
  };
};
